import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import useApi from "../../../../utils/api";
import { getErrorMessage } from "../../../../utils/helpers/errors";

export const useTimeOffHistoryApi = () => {
    const { t } = useTranslation();
    const [plans, setPlans] = useState([]);
    const [history, setHistory] = useState();
    const [loadingPlans, setLoadingPlans] = useState(false);
    const [loadingHistory, setLoadingHistory] = useState(false);

    const { authGet } = useApi();

    const getPlans = useCallback(
        async (url, controller, filters) => {
            setLoadingPlans(true);
            try {
                const response = await authGet(url, {
                    signal: controller.signal,
                    params: {
                        pagination: false,
                        bringAllPlans: true,
                        ...filters,
                    }
                });
                if (response) {
                    setPlans(response?.result || response);
                }
            } catch (error) {
                getErrorMessage(error, t);
            } finally {
                setLoadingPlans(false);
            }
        },
        [authGet, t]
    );

    const getHistory = useCallback(
        async (url, params, controller) => {
            setLoadingHistory(true);
            try {
                const response = await authGet(url, {
                    params,
                    signal: controller.signal,
                });
                if (response) {
                    setHistory(response);
                }
            } catch (error) {
                getErrorMessage(error, t);
            } finally {
                setLoadingHistory(false);
            }
        },
        [authGet, setLoadingHistory, t]
    );

    return {
        getPlans,
        getHistory,
        plans,
        history,
        loadingHistory,
        loadingPlans,
    };
};

// Leave balance ledger (TR-441) for one user time off plan.
// from / to are optional YYYY-MM-DD strings; the backend defaults them to the plan start and today.
export const useLedgerHistoryApi = () => {
    const { t } = useTranslation();
    const [ledger, setLedger] = useState(null);
    const [loading, setLoading] = useState(false);

    const { authGet } = useApi();

    const getLedger = useCallback(
        async ({ isSelf, userId, planId, from, to }, controller) => {
            const url = isSelf
                ? `/time-off-plan/ledger/profile/${planId}`
                : `/time-off-plan/ledger/user/${userId}/${planId}`;
            setLoading(true);
            try {
                const response = await authGet(url, {
                    params: { from: from || undefined, to: to || undefined },
                    signal: controller?.signal,
                });
                if (response) {
                    setLedger(response);
                }
            } catch (error) {
                if (!controller?.signal?.aborted) {
                    setLedger(null);
                    getErrorMessage(error, t);
                }
            } finally {
                if (!controller?.signal?.aborted) {
                    setLoading(false);
                }
            }
        },
        [authGet, t]
    );

    return { getLedger, ledger, loading };
};
